/* ── Bridge to the client-supplied feedback HTML ───────────────────────────────
   public/babyreveal-feedback-forms.html is shipped by the client and must stay
   untouched so a new drop can be copied over it verbatim. It renders both forms
   ("I took the test" / "I watched a reveal") behind a segmented switch and only
   console.logs on submit.

   This module reaches into that document — same origin, so the iframe DOM is
   reachable — and applies at runtime what the app needs: lock the page to the
   participant's role, map their identity in, and read a submission back out.

   Every lookup below degrades gracefully. If a later revision renames an id,
   drops the switch, or splits the panels across two pages, the affected step is
   skipped and the rest still works — capturing the submission never depends on
   the layout hooks.
─────────────────────────────────────────────────────────────────────────────── */

const OTHER_FORM = { guests: "parents", parents: "guests" };

// Fallback panel ids, used only if the `data-form` attribute ever disappears.
const PANEL_ID = { guests: "panel-guests", parents: "panel-parents" };

// Kept out of the answers blob — these become the record's own columns.
const IDENTITY_FIELDS = { full_name: "name", email: "email" };

const STYLE_ID = "br-bridge-style";

/* ── small DOM helpers ────────────────────────────────────────────────────── */

const attrEscape = (value) => String(value).replace(/["\\]/g, "\\$&");

// Visible text with the form's decorative bits stripped, so a label like
// `Your name <span class="opt">Optional</span>` reads as "Your name".
function textOf(node) {
  if (!node) return "";
  const copy = node.cloneNode(true);
  copy.querySelectorAll(".opt, .hint, .private, svg").forEach((n) => n.remove());
  return copy.textContent.replace(/\s+/g, " ").trim();
}

const prettify = (name) =>
  String(name).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ── locating the two forms ───────────────────────────────────────────────── */

function findForm(doc, formType) {
  const form = doc.querySelector(`form[data-form="${formType}"]`);
  const panel = form?.closest(".panel") || doc.getElementById(PANEL_ID[formType]);
  return { form, panel };
}

// Switch buttons point at their panel through aria-controls, which survives an
// id rename on either side.
function findTab(doc, panel) {
  if (!panel?.id) return null;
  return doc.querySelector(`.switch [aria-controls="${attrEscape(panel.id)}"]`);
}

function injectStyle(doc) {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  // Uses the file's own tokens: the locked tab stays legible but clearly out of
  // reach, and mapped identity fields read as filled in rather than editable.
  style.textContent = `
    .switch button[disabled]{opacity:.45;cursor:not-allowed;}
    .switch button[disabled]:hover{background:transparent;box-shadow:none;}
    input[readonly],textarea[readonly]{background:#F7F2EF;color:#5F5866;cursor:default;}
    input[readonly]:focus{border-color:#EAE0DC;box-shadow:none;}
    .consent input[readonly]{background:rgba(253,247,244,.05);color:rgba(253,247,244,.62);}
  `;
  (doc.head || doc.documentElement).appendChild(style);
}

/* ── 1. lock the document to one role ─────────────────────────────────────────
   An attendee may only fill "I watched a reveal"; the host may only fill "I took
   the test". The other tab stays visible — so it is clear the other form exists
   — but is disabled, and every control in its panel is disabled too, so it
   cannot be filled even if something un-hides it. */
export function lockFormForRole(doc, formType) {
  injectStyle(doc);

  const mine = findForm(doc, formType);
  const theirs = findForm(doc, OTHER_FORM[formType]);

  // Select my panel through the file's own tab handler where possible, so its
  // aria state and any future switch behaviour stay consistent.
  const myTab = findTab(doc, mine.panel);
  if (myTab) {
    if (myTab.getAttribute("aria-selected") !== "true") myTab.click();
  } else if (mine.panel) {
    mine.panel.hidden = false;
  }

  if (theirs.panel) {
    theirs.panel.hidden = true;
    theirs.panel
      .querySelectorAll("input, select, textarea, button")
      .forEach((el) => { el.disabled = true; });
  }

  const theirTab = findTab(doc, theirs.panel);
  if (theirTab) {
    // A disabled <button> dispatches no click, so the file's own switch handler
    // can never fire for it — the tab cannot be switched.
    theirTab.disabled = true;
    theirTab.setAttribute("aria-disabled", "true");
    theirTab.setAttribute("aria-selected", "false");
    theirTab.tabIndex = -1;
    theirTab.title =
      formType === "guests"
        ? "Only the person who took the test can fill this form."
        : "Only reveal guests can fill this form.";
  }

  return mine.form;
}

/* ── 2. map the participant's known details in ────────────────────────────────
   Read-only rather than disabled: a disabled control is dropped from the form's
   own FormData, and this identity is what ties the row back to the meeting. */
export function prefillIdentity(form, { name, email }) {
  if (!form) return;
  const fill = (selector, value) => {
    const el = form.querySelector(selector);
    if (!el || !value) return;
    el.value = value;
    el.readOnly = true;
  };
  fill('input[name="full_name"]', name);
  fill('input[name="email"]', email);
}

/* ── 3. read a submission back out ────────────────────────────────────────────
   Each answer is stored as { name, question, answer, value }:
     question/answer — the wording as a human read it, lifted from the DOM at
       submit time rather than a hard-coded list, so a redrafted HTML file with
       new or reworded questions stores and renders correctly with no code change
     value — the raw control value(s), which is what replays the answer back into
       the form later. Kept separate because label text is not reversible: an
       option like "No sound, or sound problems" contains the very separator that
       joins a multi-select answer. */
export function readSubmission(form) {
  const doc = form.ownerDocument;
  const identity = {};
  const grouped = new Map(); // field name → { question, labels[], values[] }

  form.querySelectorAll("input, select, textarea").forEach((el) => {
    if (!el.name || el.disabled) return;
    if ((el.type === "radio" || el.type === "checkbox") && !el.checked) return;

    const raw = String(el.value ?? "").trim();
    if (!raw) return;

    if (IDENTITY_FIELDS[el.name]) {
      identity[IDENTITY_FIELDS[el.name]] = raw;
      return;
    }

    if (!grouped.has(el.name)) {
      grouped.set(el.name, { question: questionFor(el, doc), labels: [], values: [] });
    }
    grouped.get(el.name).labels.push(answerFor(el, raw));
    grouped.get(el.name).values.push(raw);
  });

  const answers = [...grouped.entries()].map(([name, entry]) => {
    const answer = entry.labels.join(", ");
    return {
      name,
      question: entry.question,
      // A standalone consent checkbox labels itself, so question and answer come
      // back identical — record it as the yes/no it actually is.
      answer: answer === entry.question ? "Yes" : answer,
      value: entry.values.length > 1 ? entry.values : entry.values[0],
    };
  });

  return { answers, name: identity.name || "", email: identity.email || "" };
}

// A field row can contain a nested row — the form reveals follow-up questions
// inside a `.cond` block — so a legend only counts if this row is its own
// nearest row, not an ancestor of the one it really belongs to.
function ownedBy(row, selector) {
  return [...row.querySelectorAll(selector)].find(
    (el) => el.closest(".f, .rrow") === row
  );
}

/* ── 4. replay a stored submission (host's read-only view) ────────────────────
   Writes the answers back into the real form and dispatches `change` on each
   control it touches, so the file's own scripts light up the dot ratings and
   reveal the conditional follow-up blocks exactly as they looked when the
   attendee filled it in. Controls are walked in document order, so a consent
   checkbox always un-hides its follow-up before that follow-up is filled. */
export function applySubmission(form, answers) {
  if (!form || !Array.isArray(answers)) return;

  const byName = new Map();
  answers.forEach((entry) => {
    if (entry?.name) byName.set(entry.name, entry);
  });

  form.querySelectorAll("input, select, textarea").forEach((el) => {
    const entry = byName.get(el.name);
    if (!entry) return;

    if (el.type === "radio" || el.type === "checkbox") {
      if (!matches(el, entry)) return;
      el.checked = true;
    } else if (el.tagName === "SELECT") {
      const option = [...el.options].find(
        (o) => rawValues(entry).includes(o.value) || o.text.trim() === entry.answer
      );
      if (!option) return;
      el.value = option.value;
    } else {
      const [value] = rawValues(entry);
      el.value = value ?? entry.answer ?? "";
    }

    el.dispatchEvent(new el.ownerDocument.defaultView.Event("change", { bubbles: true }));
  });
}

// Raw values recorded at submit time. Rows written before `value` existed fall
// back to label matching in matches() below.
function rawValues(entry) {
  if (entry.value === undefined || entry.value === null) return [];
  return (Array.isArray(entry.value) ? entry.value : [entry.value]).map(String);
}

function matches(el, entry) {
  const values = rawValues(entry);
  if (values.length) return values.includes(String(el.value));

  // Legacy row: compare against the option's own wording.
  const label = answerFor(el, String(el.value));
  const answer = String(entry.answer || "");
  return answer === label || answer.split(", ").includes(label);
}

/* Turns the whole document into a read-only record: every control disabled, and
   the submit button removed so nothing can be re-sent. */
export function makeReadOnly(doc) {
  doc.querySelectorAll("input, select, textarea, button").forEach((el) => {
    el.disabled = true;
  });
  doc.querySelectorAll(".submit-row").forEach((el) => { el.hidden = true; });
}

/* Which of the two forms a stored submission came from. formType is recorded on
   every new row; for anything older, the answer field names are matched against
   both forms and the better fit wins. */
export function detectFormType(doc, answers, storedFormType) {
  if (storedFormType) return storedFormType;

  const score = (formType) => {
    const form = doc.querySelector(`form[data-form="${formType}"]`);
    if (!form) return 0;
    return (answers || []).filter(
      (entry) => entry?.name && form.querySelector(`[name="${attrEscape(entry.name)}"]`)
    ).length;
  };

  return score("parents") > score("guests") ? "parents" : "guests";
}

// The question a control belongs to: the group legend for radio/checkbox and
// rating sets, the field's own <label> for free text and selects, and — for a
// lone checkbox that states its own case, like the publish consent — the text
// of the choice itself.
function questionFor(el, doc) {
  const row = el.closest(".f, .rrow");

  if (row) {
    const legend = ownedBy(row, ".q, .rlabel");
    if (legend) return textOf(legend);
  }

  if (el.id) {
    const own = textOf(doc.querySelector(`label[for="${attrEscape(el.id)}"]`));
    if (own) return own;
  }

  if (row) {
    const fallback = textOf(ownedBy(row, "label"));
    if (fallback) return fallback;
  }

  return prettify(el.name);
}

// The answer as a human would read it: the chosen option's label, the selected
// option's text, or the typed value.
function answerFor(el, raw) {
  if (el.type === "radio" || el.type === "checkbox") {
    const own = el.id
      ? el.ownerDocument.querySelector(`label[for="${attrEscape(el.id)}"]`)
      : null;
    const label = own || el.closest("label");
    const text = textOf(label);
    if (text) return text;
    // Dot ratings have empty labels and carry their meaning in aria-label ("4 of 5").
    return label?.getAttribute("aria-label") || raw;
  }

  if (el.tagName === "SELECT") {
    return el.options[el.selectedIndex]?.text?.trim() || raw;
  }

  return raw;
}
